import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api-service';
import { HttpClient } from '@angular/common/http';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { AuthService } from './auth.service';
import { shareReplay } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { MainFilterItem } from 'app/components/new-navigation/rankings/models/mainFilterItem';
import { CapitalizePipe } from 'app/pipes/capitalize.pipe';
import { ReportConfig } from 'app/components/new-navigation/rankings/REPORT_CONFIG';

@Injectable({
  providedIn: 'root'
})
export class DeveloperService extends BaseApiService {
  dataCache: any;
  capitalizePipe: CapitalizePipe = new CapitalizePipe();

  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
    private titleService: Title,
    private metaService: Meta
  ) {
    super();
    this.dataCache = {};
  }

  public setPageDocumentMeta(reportConfig: ReportConfig): void {
    if (reportConfig) {
      this.titleService.setTitle(reportConfig.pageTitle);
      const tag: MetaDefinition = {
        name: 'description',
        content: reportConfig.pageMeta
      };
      this.metaService.updateTag(tag);
    }
  }

  public getRankingsData(
    reportId: string,
    start,
    limit,
    additionalQueryString: string
  ) {
    const urlString =
      `${this.getRatingsBaseUrl('2020')}/getRankings?reportId=${reportId}` +
      `&${additionalQueryString}&start=${start}&limit=${limit}`;

    return this.getCachedObservable(urlString);
  }

  public getDeveloperData(developerName: string, selectedDate: string) {
    let query = `WHERE developerName = '${developerName}'`;
    if (selectedDate) {
      query += ` AND month='${selectedDate.slice(0, 6)}'`;
    }
    const urlString = `${this.getReportBaseUrl(
      '2018'
    )}/getWidget?q=${query}&reportId=mobileAppKyd&widgetId=mobileAppKydSummary`;

    return this.getCachedObservable(urlString);
  }

  public loadMonthOptions(
    currentConfig: ReportConfig,
    mainFilterItems: Array<MainFilterItem>,
    monthFilterItem: MainFilterItem,
    selectedGroup: string
  ) {
    return new Promise(resolve => {
      const facets = [];
      facets.push('facet=date');
      let additionalQueryParams = '';
      const queryParams = [];
      if (monthFilterItem && monthFilterItem.dependencies) {
        const foundDependencyList = monthFilterItem.dependencies;
        if (foundDependencyList && foundDependencyList.length > 0) {
          foundDependencyList.forEach(foundDependency => {
            const foundDependencyFilter = mainFilterItems.filter(
              filter => foundDependency.key === filter.key
            );
            if (foundDependencyFilter && foundDependencyFilter[0]) {
              const value = foundDependencyFilter[0].selected.value;
              queryParams.push(`${foundDependency.key}=${value}`);
            }
          });
        }
      }
      if (queryParams.length > 0) {
        additionalQueryParams = queryParams.join('&');
      }
      const reportId = currentConfig.reportId;
      void this.getRankingsFacets(
        facets,
        reportId,
        selectedGroup,
        additionalQueryParams
      ).then(response => {
        const items = response['facets'][monthFilterItem.key];
        if (items && items.length > 0) {
          items.forEach(item => {
            const keys = Object.keys(item);
            const option: any = {};
            option.label = this.getMonthOptionLabel(reportId, keys[0]);
            option.value = keys[0];

            monthFilterItem.options.push(option);
          });
          monthFilterItem.options = monthFilterItem.options.sort(
            (option1, option2) => option2.value.localeCompare(option1.value)
          );
          monthFilterItem.selected = monthFilterItem.options[0];

          this.setFilterItemSelected(monthFilterItem);

          // Load Facets based on dependency key and group
          const promises = [];
          mainFilterItems.forEach(mainFilterItem => {
            if (
              mainFilterItem &&
              mainFilterItem.dependencies &&
              mainFilterItem.dependencies.length > 0
            ) {
              const foundDependency = mainFilterItem.dependencies.some(
                dependency => dependency.key === monthFilterItem.key
              );
              if (foundDependency) {
                promises.push(
                  this.loadOptions(mainFilterItem, currentConfig, selectedGroup)
                );
              }
            }
          });
          if (promises.length > 0) {
            void Promise.all(promises).then(() => {
              resolve(true);
            });
          } else {
            resolve(true);
          }
        }
      });
    });
  }

  public loadOptions(
    mainFilterItem: MainFilterItem,
    currentConfig: ReportConfig,
    selectedGroup: string,
    route?: ActivatedRoute
  ) {
    return new Promise(resolve => {
      const facets = [];
      facets.push(`facet=${mainFilterItem?.key}`);

      // Get the parameters based on dependency key and group
      let additionalParams = '';
      const addParams = new Map();
      const mainFilterItems = currentConfig.mainFilterItems;
      if (
        mainFilterItem.dependencies &&
        mainFilterItem.dependencies.length > 0
      ) {
        mainFilterItem.dependencies.forEach(dependency => {
          mainFilterItems
            .filter(
              item =>
                ((dependency.key && item.key === dependency.key) ||
                  (dependency.label && item.label === dependency.label)) &&
                item.selected &&
                item.selected.value !== ''
            )
            .forEach(item => {
              let value = item.selected.value;
              if (route && route.snapshot.params[item.routeParam]) {
                value = route.snapshot.params[item.routeParam];
              }
              addParams.set(item?.apiKey || item?.key, value);
            });
        });
      }

      if (addParams.size > 0) {
        additionalParams = this.mapToUrlParam(addParams);
      }

      void this.getRankingsFacets(
        facets,
        currentConfig.reportId,
        selectedGroup,
        additionalParams
      ).then(response => {
        const newOptions = [];
        const facetKey = mainFilterItem?.apiKey || mainFilterItem?.key;
        if (response && response['facets'] && response['facets'][facetKey]) {
          const facetOptions = response['facets'][facetKey];
          if (facetOptions && facetOptions.length > 0) {
            facetOptions.forEach(facetOption => {
              const option: any = {};
              const keys = Object.keys(facetOption);
              option.value = keys[0];
              option.label = facetOption.name || option.value;
              option.name = facetOption.name || option.value;

              if (mainFilterItem.label === 'Country') {
                if (facetOption.name) {
                  option.label = `<img alt="${option.label}" class="mr-1" width="18" src="https://cdn.pixalate.com/icons/countries/flat/48/${option.value}.png" onerror="this.src='/assets/icons/icon-flag.png';" /> ${option.label}`;
                } else {
                  option.label = this.capitalizePipe.capitalize(option.label);
                }
              }

              newOptions.push(option);
            });
          }
        }

        // Add global values, and Apply Sorting
        if (mainFilterItem.key === 'date') {
          mainFilterItem.options = newOptions.sort((option1, option2) =>
            option2.value.localeCompare(option1.value)
          );
        } else if (mainFilterItem.label === 'Country') {
          mainFilterItem.options = [];
          mainFilterItem.options.push({
            label: 'All Countries',
            value: ''
          });
          mainFilterItem.options = mainFilterItem.options.concat(
            newOptions.sort((option1, option2) => {
              return option1.name.localeCompare(option2.name, 'en', {
                sensitivity: 'base'
              });
            })
          );
        }

        this.setFilterItemSelected(mainFilterItem);
        resolve(true);
      });
    });
  }

  public loadRiskStats(reportId: string, addParams: Map<any, any>) {
    const facets = ['facet=overallRisk'];
    const selectedGroup = addParams.get('group');
    const params = new Map(addParams);
    params.delete('overallRisk');

    const additionalQueryString = this.mapToUrlParam(params);

    return new Promise(resolve => {
      void this.getRankingsFacets(
        facets,
        reportId,
        selectedGroup,
        additionalQueryString
      ).then(response => {
        resolve(response['facets']);
      });
    });
  }

  private getRankingsFacets(
    facets: Array<string>,
    reportId: string,
    group: string,
    additionalQueryParams?: string
  ) {
    return new Promise(resolve => {
      const facetsString = facets.join('&');
      let url = `${this.getRatingsBaseUrl(
        '2020'
      )}/getRankingsFacets?reportId=${reportId}&group=${group}&${facetsString}`;

      if (additionalQueryParams && additionalQueryParams.length > 0) {
        url += `&${additionalQueryParams}`;
      }

      if (this.dataCache[url]) {
        setTimeout(() => {
          const cacheData = this.dataCache[url];
          resolve(JSON.parse(JSON.stringify(cacheData)));
        }, 300);
      } else {
        this._http.get(url).subscribe(res => {
          this.storeCache(res, this.dataCache, url);
          resolve(res);
        });
      }
    });
  }

  private getCachedObservable(urlString: string) {
    const cacheKey = `${this._auth.getAuthState()}:${urlString}`;
    let cachedObservable = this.dataCache[cacheKey];

    if (!cachedObservable) {
      cachedObservable = this._http.get(urlString).pipe(shareReplay());
      this.dataCache[cacheKey] = cachedObservable;
    }

    return cachedObservable.toPromise();
  }
}
